import React from 'react';
import { motion } from 'framer-motion';
import { useJourney } from '../JourneyContext';
import { WIZARD_TEMPLATES } from '../templates';
import { Card } from '../../ui';

// Icon components for templates
const TemplateIcon = ({ type, className }) => {
  const icons = {
    building: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>
    ),
    corporate: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
      </svg>
    ),
    cloud: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
      </svg>
    ),
    rocket: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.59 14.37a6 6 0 01-5.84 7.38v-4.8m5.84-2.58a14.98 14.98 0 006.16-12.12A14.98 14.98 0 009.631 8.41m5.96 5.96a14.926 14.926 0 01-5.841 2.58m-.119-8.54a6 6 0 00-7.381 5.84h4.8m2.581-5.84a14.927 14.927 0 00-2.58 5.84m2.699 2.7c-.103.021-.207.041-.311.06a15.09 15.09 0 01-2.448-2.448 14.9 14.9 0 01.06-.312m-2.24 2.39a4.493 4.493 0 00-1.757 4.306 4.493 4.493 0 004.306-1.758M16.5 9a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
      </svg>
    ),
    edit: (
      <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
      </svg>
    ),
  };
  return icons[type] || icons.edit;
};

// Color classes for templates
const colorClasses = {
  blue: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-blue-500 dark:text-blue-400',
    hover: 'hover:border-blue-400 dark:hover:border-blue-600',
    selected: 'ring-2 ring-blue-500 border-blue-500',
  },
  purple: {
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-200 dark:border-purple-800',
    icon: 'text-purple-500 dark:text-purple-400',
    hover: 'hover:border-purple-400 dark:hover:border-purple-600',
    selected: 'ring-2 ring-purple-500 border-purple-500',
  },
  cyan: {
    bg: 'bg-cyan-50 dark:bg-cyan-900/20',
    border: 'border-cyan-200 dark:border-cyan-800',
    icon: 'text-cyan-500 dark:text-cyan-400',
    hover: 'hover:border-cyan-400 dark:hover:border-cyan-600',
    selected: 'ring-2 ring-cyan-500 border-cyan-500',
  },
  green: {
    bg: 'bg-green-50 dark:bg-green-900/20',
    border: 'border-green-200 dark:border-green-800',
    icon: 'text-green-500 dark:text-green-400',
    hover: 'hover:border-green-400 dark:hover:border-green-600',
    selected: 'ring-2 ring-green-500 border-green-500',
  },
  gray: {
    bg: 'bg-gray-50 dark:bg-gray-800/50',
    border: 'border-gray-200 dark:border-gray-700',
    icon: 'text-gray-500 dark:text-gray-400',
    hover: 'hover:border-gray-400 dark:hover:border-gray-500',
    selected: 'ring-2 ring-gray-500 border-gray-500',
  },
};

export default function QuickSetupStep({ onNext }) {
  const { selectedTemplate, applyTemplate, startNewDraft } = useJourney();

  const handleSelectTemplate = (template) => {
    if (!selectedTemplate) {
      startNewDraft();
    }
    applyTemplate(template);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div>
      <Card className="p-6 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Choose Your Setup
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Select a template to get started quickly, or start from scratch for full control.
          </p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {WIZARD_TEMPLATES.map((template) => {
            const colors = colorClasses[template.color] || colorClasses.gray;
            const isSelected = selectedTemplate === template.id;

            return (
              <motion.div
                key={template.id}
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <button
                  type="button"
                  onClick={() => handleSelectTemplate(template)}
                  className={`
                    w-full p-5 rounded-xl border-2 text-left transition-all duration-200
                    ${colors.bg} ${colors.border} ${colors.hover}
                    ${isSelected ? colors.selected : ''}
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${colors.bg} ${colors.icon}`}>
                      <TemplateIcon type={template.icon} className="w-8 h-8" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {template.name}
                        </h3>
                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className={`w-6 h-6 rounded-full ${colors.icon} flex items-center justify-center`}
                          >
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </motion.div>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {template.description}
                      </p>
                      
                      {/* Template preview details */}
                      {template.defaults && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {template.defaults.systems?.slice(0, 3).map((sys) => (
                            <span
                              key={sys}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-white/60 dark:bg-gray-700/60 text-gray-700 dark:text-gray-300"
                            >
                              {sys}
                            </span>
                          ))}
                          {template.defaults.systems?.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{template.defaults.systems.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Selected template summary */}
        {selectedTemplate && selectedTemplate !== 'scratch' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="mt-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800"
          >
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm font-medium">
                Template selected! Your form will be pre-filled with recommended settings.
              </span>
            </div>
          </motion.div>
        )}
      </Card>
    </div>
  );
}

